<template>
    <div class="panel">
        <b-table :data="permittings" :loading="isLoadingTable" paginated backend-pagination :per-page="perPage"
            :total="total" @page-change="onPageChange" backend-sorting :default-sort="orderField"
            :default-sort-direction="orderDir" @sort="onSort" class="w-full" striped bordered icon-pack="fas"
            scrollable>
            <template>
                <b-table-column field="id" :label="$t('id')" sortable v-slot="props">
                    {{ props.row.id }}
                </b-table-column>
                <b-table-column field="code" :label="$t('code')" sortable v-slot="props">
                    {{ props.row.template.code }}
                </b-table-column>
                <b-table-column field="activity" :label="$t('activity')" sortable v-slot="props">
                    {{ props.row.activity?.name || '-' }}
                </b-table-column>
                <b-table-column field="planned_start" :label="$t('planned_start')" v-slot="props">
                    {{ formatDate(props.row.activity?.current_start_date) }}
                </b-table-column>
                <b-table-column field="planned_end" :label="$t('planned_end')" v-slot="props">
                    {{ formatDate(props.row.activity?.current_end_date) }}
                </b-table-column>
                <b-table-column field="actual_start" :label="$t('actual_start')" v-slot="props">
                    {{ formatDate(props.row.activity?.gantt_real_start_date) }}
                </b-table-column>
                <b-table-column field="actual_end" :label="$t('actual_end')" v-slot="props">
                    {{
                        props.row.activity?.progress_actual >= 100 && props.row.activity?.gantt_real_end_date
                            ? formatDate(props.row.activity.gantt_real_end_date)
                            : '-'
                    }}
                </b-table-column>
                <b-table-column field="response_date" :label="$t('response_date')" v-slot="props">
                    <div class="flex flex-col items-center">
                        <div>{{ formatDate(props.row.response_date) }}</div>
                        <div class="mt-1">
                            <b-tag :type="getStatusType(props.row.status)">
                                {{ getStatusLabel(props.row.status) }}
                            </b-tag>
                        </div>
                    </div>
                </b-table-column>
                <b-table-column field="expiration_date" :label="$t('expiration_date')" v-slot="props">
                    {{ formatDate(props.row.expiration_date) }}
                </b-table-column>
                <b-table-column field="actions" :label="$t('actions')" v-slot="props" centered>
                    <div class="w-full h-full flex items-center justify-around">
                        <b-tooltip :label="$t('edit')" position="is-left">
                            <div @click="editClicked(props.row)" class="cursor-pointer flex text-blue">
                                <b-icon pack="fas" icon="pencil-alt" />
                            </div>
                        </b-tooltip>
                        <b-tooltip :label="$t('remove')" position="is-left" type="is-danger">
                            <div @click="removeClicked(props.row)" class="cursor-pointer flex text-red">
                                <b-icon pack="fas" icon="trash-alt" />
                            </div>
                        </b-tooltip>
                    </div>
                </b-table-column>
            </template>
            <template #empty>
                <div class="has-text-centered">{{ $t('no_results') }}</div>
            </template>
        </b-table>
    </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment';
export default {
    props: {
        projectId: { type: Number, require: true },
    },
    data() {
        return {
            isLoadingTable: true,
            permittings: [],
            perPage: 25,
            orderField: 'permitting_template_id',
            orderDir: 'asc',
            page: 1,
            last: 1,
            total: 0,
        }
    },
    created () {
        this.getPermittings();
    },
    computed: {
        filters() {
            let filters = {
                'order_field': this.orderField,
                'order_dir': this.orderDir,
                'per_page': this.perPage,
                'page': this.page,
                'project_id': this.projectId
            }
            return filters
        },
    },
    methods: {
        async getPermittings(filters = null) {
            this.isLoadingTable = true;
            const params = filters ? filters : this.filters;

            try {
                const { data } = await axios.get(`/api/v2/project/${this.projectId}/permittings`, { params });
                if (data) {
                    this.permittings = data.data
                    this.last = data.last_page;
                    this.total = data.total;
                }
            } catch (error) {
                throw ('Error fetching permittings:', error);
            } finally {
                this.isLoadingTable = false;
            }
        },
        onSort(field, order) {
            this.orderField = field;
            this.orderDir = order;
            this.getPermittings(this.filters);
        },

        onPageChange(page) {
            this.page = page;
            this.getPermittings();
        },
        editClicked(row) {
            this.$emit('edit-permitting', row);
        },
        async removeClicked(row) {
            try {
                this.$dialog.confirm({
                    title: this.$t('delete'),
                    message: this.$t('delete_confirm_text', [row.template.code]),
                    confirmText: this.$t('delete'),
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: async () => {
                        const url = `/api/v2/project/${this.projectId}/permitting/${row.id}`;
                        await axios.delete(url);
                        this.$buefy.toast.open({
                            message: this.$t('removed_successfully'),
                            type: 'is-success'
                        });
                        this.$emit('remove-permitting', row);
                        this.getPermittings();
                    },
                });
            } catch (error) {
                this.$buefy.toast.open({
                    message: this.$t('delete_error'),
                    type: 'is-danger'
                });
            }
        },
        formatDate(date) {
            return date ? moment(date).format('DD/MM/YYYY') : '';
        },
        getStatusLabel(status) {
            switch (status) {
                case 0: return this.$t('approved');
                case 1: return this.$t('approved_with_comments');
                case 2: return this.$t('rejected');
                case 3: return this.$t('rejected_with_comments');
                default: return '-';
            }
        },
        getStatusType(status) {
            switch (status) {
                case 0: return 'is-success';
                case 1: return 'is-info';
                case 2: return 'is-danger';
                case 3: return 'is-warning';
                default: return '';
            }
        }
    },

}
</script>

<style lang="scss" scoped></style>